import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Text, TextSmall } from 'rf-ui';
import { get } from 'rf-lib';
import type { WheelEvent } from '@lykkehjulet/db-model';
import defaultProfilePicture from './../../assets/images/default.png';

const gameInfoStyles: any = {
  stateWrapper: {
    margin: '10px 0px',
    display: 'flex',
    flexAlign: 'center',
    fontSize: '20px'
  },
  gameState: {
    backgroundColor: '#d9534f',
    borderRadius: '2px',
    margin: '0px 4px',
    padding: '5px',
    color: 'white',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    textTransform: 'uppercase'
  },
  winnerRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '5px'
  },
  winnerRowInnerMargin: {
    marginLeft: '10px'
  }
};

const PlayerProfileImage = styled.img`
  width: 64px;
  height: 64px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  `;

const GameInfoStateContainer = styled.div`
  max-height: 600px;
  min-width: 250px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.5);
  `;

const PlayerWrapper = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  `;

const PlayerName = styled.span`
  margin-left: 10px;
  `;

const ColouredBackground = styled.div`
  background: ${get('colorBackgroundSecondary')};
  margin-left: 0.2rem;
`;

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: space-between;
  margin: 3;
  padding: 3;
  background: ${get('colorBackgroundSecondary')};
`;

const bgActive = {
  background: 'linear-gradient(to right, transparent 50%, rgba(255, 0, 0, 0.5) 50%)',
  backgroundSize: '200% 100%',
  backgroundPosition: 'left bottom',
  transition: 'all 0.5s'
};

const bgLost = {
  ...bgActive,
  backgroundPosition: 'right bottom'
};

const Tickets = ({ tickets }: { tickets: any }) =>
  (
    <ColouredBackground>
      <TextSmall>
        {` [${tickets.current}/${tickets.start}]`}
      </TextSmall>
    </ColouredBackground>
  );

Tickets.propTypes = {
  tickets: PropTypes.object.isRequired
};

const player = (id: string, name: string, tickets: any, imageUrl: string) => (
  {
    id, name, tickets: { start: tickets, current: tickets }, imageUrl
  }
);

const getPlayer = (uid: string, allPlayers: any[]) => allPlayers.find(x => x.uid === uid);

const mapPlayers = (game: any, players: any[]) => {
  let gamePlayers: any[] = [];
  if (game.tickets) {
    gamePlayers = Object.keys(game.tickets || {})
      .filter(key => {
        if (game.tickets[key] === 0) {
          return false;
        }
        return true;
      })
      .map(
        key => {
          const { displayName = '<Unknown>', imageUrl = '', } = getPlayer(key, players) || {};
          return player(
            key, displayName, game.tickets[key],
            imageUrl.length > 0 ? imageUrl : defaultProfilePicture
          );
        }
      )
      .reverse();
  }
  return gamePlayers;
};

const mapWinners = (winnerList: any[], players: any[]) =>
  winnerList
    .map(({ playerId }) => {
      const { displayName = '<Unknown>', imageUrl = '', } = getPlayer(playerId, players) || {};
      return player(
        playerId, displayName, null,
        imageUrl.length > 0 ? imageUrl : defaultProfilePicture
      );
    });

const applyTicketDelta = (
  acc: any,
  playerId: string,
  delta: number,
  eventIndex: number
) => {
  const previous = acc[playerId] || { delta: 0, lastEventIndex: -1 };

  acc[playerId] = {
    delta: previous.delta + delta,
    lastEventIndex: eventIndex
  };
};

const groupTicketDelta = (events: any[]) => events
  .reduce((acc: any, event: WheelEvent, index: number) => {
    if (!event) return acc;

    switch (event.type) {
      case 'out':
        if (event.playerId) {
          applyTicketDelta(acc, event.playerId, 1, index);
        }
        break;
      case 'in':
        if (event.playerId) {
          applyTicketDelta(acc, event.playerId, -1, index);
        }
        break;
      case 'gifts':
        event.playerIds.forEach((playerId: string) => {
          applyTicketDelta(acc, playerId, -1, index);
        });
        break;
      default:
        break;
    }

    return acc;
  }, {});

const mapTicketCount = (players: any[], groupedTicketDelta: any) => (
  players
    .map(p => {
      const playerDelta = groupedTicketDelta[p.id] || { delta: 0, lastEventIndex: -1 };
      const tickets = {
        ...p.tickets,
        current: p.tickets.start - playerDelta.delta
      };

      return ({ ...p, lastEventIndex: playerDelta.lastEventIndex, tickets });
    })
);

const compare = (a: any, b: any) => {
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};

const GameInfoState = ({
  gameRunner,
  lastShownAnimation,
  game,
  allPlayers
}: {
  gameRunner: any;
  lastShownAnimation: number;
  game: any;
  allPlayers: any[];
}) => {
  const gameStateLowerCase = gameRunner.gameState ? gameRunner.gameState.toLowerCase() : '';

  const events = gameRunner.events
    ? gameRunner.events.slice(0, lastShownAnimation)
    : [];
  const groupedTicketDelta = groupTicketDelta(events);

  const numberOfTickets = game.tickets
    ? Object.values(game.tickets)
      .reduce(
        (accumulator: number, currentValue: unknown) => accumulator + Number(currentValue), 0
      )
    : 0;

  const gamePlayers = mapTicketCount(mapPlayers(game, allPlayers), groupedTicketDelta)
    .sort((a, b) => compare(b.lastEventIndex, a.lastEventIndex));
  const ticketsLeft = gamePlayers
    .reduce((accumulator: number, currentPlayer: any) => accumulator + Number(currentPlayer.tickets.current), 0);

  // winner tab
  if (gameStateLowerCase === 'ended'
    && lastShownAnimation === (gameRunner.events || []).length
    && gameRunner.ticketsLeft === gameRunner.numberOfPrizes) {
    const winnerList = gameRunner.winnerList ? gameRunner.winnerList : [];
    const mappedWinners = mapWinners(winnerList, allPlayers);
    return (
      <GameInfoStateContainer>
        {mappedWinners.map((winner, i) => (
          <div style={gameInfoStyles.winnerRow} key={i}>
            <PlayerProfileImage style={gameInfoStyles.playerImage} src={winner.imageUrl} alt="player profile" />
            <span style={gameInfoStyles.winnerRowInnerMargin}>{i + 1}</span>
            <span style={gameInfoStyles.winnerRowInnerMargin} role="img" aria-label="star">
              {i === 0 ? '🤩' : '⭐'}
            </span>
            <span style={gameInfoStyles.winnerRowInnerMargin}>{winner.name}</span>
          </div>
        ))}
      </GameInfoStateContainer>
    );
  }
  // still running tab
  return (
    <GameInfoStateContainer>
      <RowContainer>
        <Text>Premier: {game.numberOfPrizes}</Text>
        {gameStateLowerCase === 'not started' && <Text>Solgte lodd: {numberOfTickets}</Text>}
        {gameStateLowerCase === 'started' && <Text>Lodd igjen: {ticketsLeft}</Text>}
      </RowContainer>
      {gamePlayers.map((p, i) => (
        <PlayerWrapper key={i} style={p.tickets.current > 0 ? bgActive : bgLost}>
          <PlayerProfileImage src={p.imageUrl} />
          <Tickets tickets={p.tickets} />
          <PlayerName>{p.name}</PlayerName>
        </PlayerWrapper>
      ))}
    </GameInfoStateContainer>
  );
};

GameInfoState.propTypes = {
  gameRunner: PropTypes.object.isRequired,
  lastShownAnimation: PropTypes.number.isRequired,
  game: PropTypes.object.isRequired,
  allPlayers: PropTypes.array.isRequired
};

const GameInfo = ({
  title, gameRunner, lastShownAnimation, game, allPlayers
}: {
  title: string;
  gameRunner: any;
  lastShownAnimation: number;
  game: any;
  allPlayers: any[];
}) =>
  (
    <GameInfoState
      gameRunner={gameRunner}
      lastShownAnimation={lastShownAnimation}
      game={game}
      allPlayers={allPlayers}
    />
  );

GameInfo.propTypes = {
  title: PropTypes.string.isRequired,
  gameRunner: PropTypes.object.isRequired,
  lastShownAnimation: PropTypes.number.isRequired,
  game: PropTypes.object.isRequired,
  allPlayers: PropTypes.array.isRequired
};

export default GameInfo;
